import type {
  ResultHighlight,
  SearchDocument,
} from '@backstage/plugin-search-common';
import type { JsonObject } from '@backstage/types';
import { BookOpen, Boxes, FileSearch, type LucideIcon } from 'lucide-react';

export const RESULT_TYPES: ReadonlyArray<{
  value: string;
  label: string;
  icon: LucideIcon;
}> = [
  { value: 'software-catalog', label: 'Software Catalog', icon: Boxes },
  { value: 'techdocs', label: 'Documentation', icon: BookOpen },
];

export const resultTypeIcon = (type: string): LucideIcon =>
  RESULT_TYPES.find(entry => entry.value === type)?.icon ?? FileSearch;

export const resultTypeLabel = (type: string): string =>
  RESULT_TYPES.find(entry => entry.value === type)?.label ?? type;

export const KIND_VALUES = [
  'API',
  'Component',
  'Domain',
  'Group',
  'Location',
  'Resource',
  'System',
  'Template',
  'User',
] as const;

export const LIFECYCLE_VALUES = ['experimental', 'production'] as const;

/**
 * Search documents are only typed down to `{title, text, location}`; collators
 * add their own fields, so the extras are read defensively.
 */
export const documentFacets = (document: SearchDocument) => {
  const record = document as SearchDocument & Record<string, unknown>;
  const read = (key: string) =>
    typeof record[key] === 'string' && record[key] ? String(record[key]) : undefined;

  return [
    { label: 'Kind', value: read('kind') },
    { label: 'Type', value: read('componentType') ?? read('type') },
    { label: 'Lifecycle', value: read('lifecycle') },
    { label: 'Owner', value: read('owner') },
  ].filter((facet): facet is { label: string; value: string } =>
    Boolean(facet.value),
  );
};

export type Segment = { key: string; text: string; match: boolean };

const MAX_SNIPPET_LENGTH = 260;

/**
 * The backend marks matched terms by wrapping them in opaque `preTag`/`postTag`
 * strings. They are split into plain text nodes rather than injected as HTML —
 * the excerpt is indexed user content and must never reach `innerHTML`.
 */
export const snippetSegments = (
  document: SearchDocument,
  highlight?: ResultHighlight,
): Segment[] => {
  const source = highlight?.fields?.text ?? document.text ?? '';
  const { preTag, postTag } = highlight ?? {};

  const raw: Omit<Segment, 'key'>[] = [];
  let rest = source;

  if (preTag && postTag) {
    for (
      let start = rest.indexOf(preTag);
      start !== -1;
      start = rest.indexOf(preTag)
    ) {
      const end = rest.indexOf(postTag, start);
      if (end === -1) break;
      if (start > 0) raw.push({ text: rest.slice(0, start), match: false });
      raw.push({ text: rest.slice(start + preTag.length, end), match: true });
      rest = rest.slice(end + postTag.length);
    }
  }
  if (rest) raw.push({ text: rest, match: false });

  const segments: Segment[] = [];
  let offset = 0;
  for (const segment of raw) {
    const budget = MAX_SNIPPET_LENGTH - offset;
    if (budget <= 0) {
      segments.push({ key: `${offset}-ellipsis`, text: '…', match: false });
      break;
    }
    const text = segment.text.slice(0, budget);
    segments.push({ ...segment, text, key: `${offset}-${segment.match}` });
    offset += segment.text.length;
  }

  return segments;
};

/* ── URL state ─────────────────────────────────────────────── */

const TYPE_PARAM = 'type';
const FILTER_PREFIX = 'filter.';

export type SearchUrlState = {
  term: string;
  types: string[];
  filters: JsonObject;
};

/**
 * Upstream's `UrlUpdater` is not exported, so the portal owns the query-string
 * contract. `query` matches upstream so existing deep links keep working;
 * filters use readable `filter.<name>` params instead of `qs` bracket syntax.
 */
export const readSearchUrlState = (search: string): SearchUrlState => {
  const params = new URLSearchParams(search);
  const filters: JsonObject = {};

  for (const key of new Set(params.keys())) {
    if (!key.startsWith(FILTER_PREFIX)) continue;
    const name = key.slice(FILTER_PREFIX.length);
    const values = params.getAll(key);
    filters[name] = values.length > 1 ? values : values[0];
  }

  return {
    term: params.get('query') ?? '',
    types: params.getAll(TYPE_PARAM),
    filters,
  };
};

export const writeSearchUrlState = ({
  term,
  types,
  filters,
}: SearchUrlState): string => {
  const params = new URLSearchParams();
  if (term) params.set('query', term);
  types.forEach(type => params.append(TYPE_PARAM, type));

  for (const [name, value] of Object.entries(filters)) {
    const values = Array.isArray(value) ? value : [value];
    for (const entry of values) {
      const primitive =
        typeof entry === 'string' ||
        typeof entry === 'number' ||
        typeof entry === 'boolean';
      if (!primitive || entry === '') continue;
      params.append(`${FILTER_PREFIX}${name}`, `${entry}`);
    }
  }

  return params.toString();
};
